import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Avatar,
  Grid,
  Card,
  CardContent,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { CorporateDialog } from '../components/ui';
import {
  PhotoCamera,
  Save,
  CloudUpload,
  Lock,
  DeleteForever,
  Warning,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { apiService } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useBackgroundImage } from '../hooks/useBackgroundImage';
import { Layout } from '../components/Layout';
import { validatePassword } from '../utils/passwordValidator';
import { PasswordRequirements } from '../components/PasswordRequirements';
import { logger } from '../utils/logger';

export const Profile: React.FC = () => {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const { showSuccess, showError } = useToast();
  const { backgroundImages, selectedBackground, changeBackground, fallbackGradients } = useBackgroundImage();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState(validatePassword(''));
  const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] = useState(false);
  const [deleteAccountLoading, setDeleteAccountLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      setFirstName(user.first_name || '');
      setLastName(user.last_name || '');
      setEmail(user.email);
      setAvatarPreview(user.avatar_url);
    }
  }, [user]);

  const compressImage = (file: File, maxWidth: number = 240, quality: number = 0.8): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Redimensionar si es necesario
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('No se pudo obtener el contexto del canvas'));
            return;
          }

          ctx.drawImage(img, 0, 0, width, height);
          const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
          resolve(compressedBase64);
        };
        img.onerror = reject;
        img.src = e.target?.result as string;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleAvatarSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validar tipo de archivo
      if (!file.type.startsWith('image/')) {
        showError(t('profile.selectImageFile'));
        return;
      }
      
      // Validar tamaño (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showError(t('profile.imageTooLarge'));
        return;
      }

      setAvatarFile(file);
      
      try {
        // Comprimir y crear preview
        const compressedBase64 = await compressImage(file);
        setAvatarPreview(compressedBase64);
      } catch (error) {
        showError(t('profile.imageProcessingError'));
        logger.error(error);
      }
    }
  };

  const handleUploadAvatar = async () => {
    if (!avatarFile) {
      showError(t('profile.selectImageFirst'));
      return;
    }

    setLoading(true);
    try {
      // Comprimir imagen antes de subir
      const compressedBase64 = await compressImage(avatarFile);
      
      // Verificar que el base64 no sea demasiado largo (máx 512 caracteres para String)
      // En realidad, vamos a usar solo los primeros 400 caracteres del base64 para evitar problemas
      // Pero mejor, vamos a usar la imagen comprimida directamente
      if (compressedBase64.length > 50000) {
        showError(t('profile.imageTooLargeCompressed'));
        setLoading(false);
        return;
      }

      await apiService.updateProfile({ avatar_url: compressedBase64 });
      await refreshUser();
      showSuccess(t('profile.avatarUpdated'));
      setAvatarFile(null);
    } catch (error: any) {
      showError(error.response?.data?.detail || t('profile.avatarUpdateError'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAvatar = async () => {
    if (!user?.avatar_url) {
      showError(t('profile.noAvatarToDelete'));
      return;
    }

    setLoading(true);
    try {
      await apiService.updateProfile({ avatar_url: null });
      await refreshUser();
      setAvatarPreview(null);
      setAvatarFile(null);
      showSuccess(t('profile.avatarDeleted'));
    } catch (error: any) {
      showError(error.response?.data?.detail || t('profile.avatarDeleteError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const updateData: any = {
        first_name: firstName || null,
        last_name: lastName || null,
      };

      await apiService.updateProfile(updateData);
      await refreshUser();
      showSuccess(t('profile.profileUpdated'));
    } catch (error: any) {
      showError(error.response?.data?.detail || t('profile.profileUpdateError'));
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      showError(t('profile.completeAllFields'));
      return;
    }

    if (newPassword !== confirmPassword) {
      showError(t('auth.passwordMismatch'));
      return;
    }

    const validation = validatePassword(newPassword);
    if (!validation.isValid) {
      showError(validation.errors.join('; '));
      return;
    }

    setPasswordLoading(true);
    try {
      await apiService.changePassword(currentPassword, newPassword);
      showSuccess(t('profile.passwordUpdated'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordDialogOpen(false);
    } catch (error: any) {
      showError(error.response?.data?.detail || t('profile.passwordChangeError'));
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleClosePasswordDialog = () => {
    if (!passwordLoading) {
      setPasswordDialogOpen(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordValidation(validatePassword(''));
    }
  };

  const handleBackgroundSelect = async (bgId: string) => {
    await changeBackground(bgId);
    showSuccess(t('profile.backgroundUpdated'));
  };

  const handleDeleteAccount = async () => {
    setDeleteAccountLoading(true);
    try {
      await apiService.deleteAccount();
      showSuccess(t('profile.accountDeleted', 'Tu cuenta ha sido eliminada permanentemente'));
      // Redirigir al login después de un breve delay
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 1500);
    } catch (error: any) {
      showError(error.response?.data?.detail || t('profile.accountDeleteError', 'Error al eliminar la cuenta'));
      setDeleteAccountLoading(false);
    }
  };

  const getInitials = () => {
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (firstName) {
      return firstName[0].toUpperCase();
    }
    if (email) {
      return email[0].toUpperCase();
    }
    return 'U';
  };

  return (
    <Layout>
      <Box sx={{ width: '100%', height: '100%', p: 3 }}>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
          {t('profile.title')}
        </Typography>

        <Grid container spacing={3}>
          {/* Sección de Avatar e Información */}
          <Grid item xs={12} md={3}>
            <Paper elevation={3} sx={{ p: 3, textAlign: 'center', height: '100%' }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                {t('profile.profilePhoto')}
              </Typography>
              <Box sx={{ position: 'relative', display: 'inline-block', mb: 3 }}>
                <Avatar
                  src={avatarPreview || undefined}
                  sx={{
                    width: 144,
                    height: 144,
                    fontSize: '3.2rem',
                    bgcolor: 'secondary.main',
                    border: '4px solid',
                    borderColor: 'primary.main',
                    mx: 'auto',
                  }}
                >
                  {!avatarPreview && getInitials()}
                </Avatar>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarSelect}
                  style={{ display: 'none' }}
                />
                <IconButton
                  color="primary"
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 'calc(50% - 90px)',
                    bgcolor: 'background.paper',
                    border: '2px solid',
                    borderColor: 'primary.main',
                    '&:hover': {
                      bgcolor: 'primary.light',
                      color: 'primary.contrastText',
                    },
                  }}
                  onClick={() => fileInputRef.current?.click()}
                  title={t('profile.selectImage')}
                >
                  <PhotoCamera />
                </IconButton>
                {user?.avatar_url && !avatarFile && (
                  <IconButton
                    color="error"
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 'calc(50% - 90px)',
                      bgcolor: 'background.paper',
                      border: '2px solid',
                      borderColor: 'error.main',
                      '&:hover': {
                        bgcolor: 'error.light',
                        color: 'error.contrastText',
                      },
                    }}
                    onClick={handleDeleteAvatar}
                    disabled={loading}
                    title={t('profile.deleteAvatar')}
                  >
                    <DeleteForever />
                  </IconButton>
                )}
              </Box>
              {avatarFile && (
                <Button
                  variant="contained"
                  startIcon={<CloudUpload />}
                  onClick={handleUploadAvatar}
                  disabled={loading}
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  {loading ? <CircularProgress size={24} /> : t('profile.uploadAvatar')}
                </Button>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {t('profile.imageFormats')}
              </Typography>
            </Paper>
          </Grid>

          {/* Sección de Información Personal */}
          <Grid item xs={12} md={9}>
            <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('profile.personalInfo')}
                </Typography>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<Lock />}
                  onClick={() => setPasswordDialogOpen(true)}
                  sx={{ ml: 2 }}
                >
                  {t('profile.changePassword')}
                </Button>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('common.firstName')}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('common.lastName')}
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('common.email')}
                    value={email}
                    disabled
                    variant="outlined"
                    helperText={t('profile.emailCannotBeModified')}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                    <Button
                      variant="contained"
                      startIcon={<Save />}
                      onClick={handleSave}
                      disabled={loading}
                    >
                      {loading ? <CircularProgress size={24} /> : t('profile.saveChanges')}
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteForever />}
                      onClick={() => setDeleteAccountDialogOpen(true)}
                      disabled={loading}
                    >
                      {t('profile.deleteAccount', 'Eliminar Cuenta')}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Sección de Fondo de Login */}
          <Grid item xs={12}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                {t('profile.backgroundImageTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('profile.backgroundImageSubtitle')}
              </Typography>
              <Grid container spacing={3}>
                {backgroundImages.map((bg) => {
                  const isSelected = selectedBackground === bg.id;
                  const fallbackGradient = fallbackGradients[bg.id] || fallbackGradients['1'];
                  
                  return (
                    <Grid item xs={12} sm={6} md={4} key={bg.id}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          border: isSelected ? 3 : 1,
                          borderColor: isSelected ? 'primary.main' : 'divider',
                          transition: 'all 0.3s ease',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: 6,
                          },
                        }}
                        onClick={() => handleBackgroundSelect(bg.id)}
                      >
                        <Box
                          sx={{
                            height: 200,
                            backgroundImage: `url(${bg.url}), ${fallbackGradient}`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                            position: 'relative',
                            borderRadius: '4px 4px 0 0',
                          }}
                        />
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                            {bg.name}
                          </Typography>
                          {isSelected && (
                            <Typography variant="caption" color="primary" sx={{ fontWeight: 600, display: 'block', mt: 1 }}>
                              {t('profile.selected')}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        {/* Dialog de Cambio de Contraseña */}
        <CorporateDialog
          open={passwordDialogOpen}
          onClose={handleClosePasswordDialog}
          title={t('profile.passwordDialogTitle')}
          icon={<Lock />}
          maxWidth="sm"
          fullWidth
          actions={
            <>
              <Button
                onClick={handleClosePasswordDialog}
                disabled={passwordLoading}
                variant="outlined"
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleChangePassword}
                disabled={
                  passwordLoading ||
                  !currentPassword ||
                  !newPassword ||
                  !confirmPassword ||
                  !passwordValidation.isValid ||
                  newPassword !== confirmPassword
                }
                variant="contained"
                color="secondary"
                startIcon={passwordLoading ? <CircularProgress size={20} /> : <Lock />}
              >
                {passwordLoading ? t('profile.changing') : t('profile.changePasswordButton')}
              </Button>
            </>
          }
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('profile.currentPassword')}
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                variant="outlined"
                autoComplete="current-password"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('profile.newPassword')}
                type="password"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setPasswordValidation(validatePassword(e.target.value));
                }}
                variant="outlined"
                error={newPassword.length > 0 && !passwordValidation.isValid}
                helperText={
                  newPassword.length > 0 && !passwordValidation.isValid
                    ? passwordValidation.errors[0]
                    : t('auth.passwordMinLengthHelper')
                }
                autoComplete="new-password"
              />
              {newPassword.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <PasswordRequirements validation={passwordValidation} />
                </Box>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('profile.confirmNewPassword')}
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                variant="outlined"
                autoComplete="new-password"
                error={confirmPassword.length > 0 && newPassword !== confirmPassword}
                helperText={
                  confirmPassword.length > 0 && newPassword !== confirmPassword
                    ? t('auth.passwordMismatch')
                    : ''
                }
              />
            </Grid>
          </Grid>
        </CorporateDialog>

        {/* Dialog de Eliminación de Cuenta */}
        <CorporateDialog
          open={deleteAccountDialogOpen}
          onClose={() => !deleteAccountLoading && setDeleteAccountDialogOpen(false)}
          title={t('profile.deleteAccountTitle', 'Eliminar Cuenta Permanentemente')}
          icon={<Warning sx={{ color: 'error.main' }} />}
          maxWidth="sm"
          fullWidth
          actions={
            <>
              <Button
                onClick={() => setDeleteAccountDialogOpen(false)}
                disabled={deleteAccountLoading}
                variant="outlined"
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleDeleteAccount}
                disabled={deleteAccountLoading}
                variant="contained"
                color="error"
                startIcon={deleteAccountLoading ? <CircularProgress size={20} /> : <DeleteForever />}
              >
                {deleteAccountLoading 
                  ? t('profile.deleting', 'Eliminando...') 
                  : t('profile.confirmDelete', 'Sí, Eliminar Permanentemente')}
              </Button>
            </>
          }
        >
          <Box>
            <Typography variant="body1" paragraph sx={{ fontWeight: 600, color: 'error.main' }}>
              {t('profile.deleteAccountWarning', '⚠️ ADVERTENCIA: Esta acción es IRREVERSIBLE')}
            </Typography>
            <Typography variant="body2" paragraph>
              {t('profile.deleteAccountMessage',
                'Al eliminar tu cuenta, se eliminarán permanentemente:')}
            </Typography>
            <Typography component="ul" sx={{ pl: 3, mb: 2 }}>
              <li>{t('profile.deleteAccountItem1', 'Todos tus datos personales')}</li>
              <li>{t('profile.deleteAccountItem2', 'Tu historial y configuraciones')}</li>
              <li>{t('profile.deleteAccountItem3', 'Tu acceso a la plataforma')}</li>
            </Typography>
            <Typography variant="body2" paragraph sx={{ fontWeight: 600, color: 'text.primary' }}>
              {t('profile.deleteAccountConfirm',
                'Esta acción no se puede deshacer. ¿Estás seguro de que deseas continuar?')}
            </Typography>
          </Box>
        </CorporateDialog>
      </Box>
    </Layout>
  );
};

